"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getInternalOAuthClientConfig = exports.OAuthClientTokenBuilder = void 0;
const AssertHelper_1 = require("./AssertHelper");
const commons_utils_1 = require("@deliverr/commons-utils");
const commons_logs_1 = require("@deliverr/commons-logs");
const ClientAuth_1 = require("./ClientAuth");
const AWS_REGION = "us-east-1";
class OAuthClientTokenBuilder {
    constructor(config) {
        const directCallerServiceName = config.directCallerServiceName || (0, commons_utils_1.getEnvString)("SERVICE_NAME", "") || (0, commons_utils_1.getEnvString)("DD_SERVICE", "");
        (0, AssertHelper_1.assert)(!!directCallerServiceName, {
            message: `Failed to determine service name which is mandatory for OAuthClientTokenBuilder`,
        });
        const deploymentEnv = config.deploymentEnv || (0, commons_utils_1.getEnvString)("ENV", "");
        (0, AssertHelper_1.assert)(!!deploymentEnv, {
            message: `Failed to determine env which is mandatory for OAuthClientTokenBuilder`,
        });
        const clientId = config.clientId || (0, commons_utils_1.getEnvString)("COGNITO_CLIENT_ID", "");
        const clientSecret = config.clientSecret || (0, commons_utils_1.getEnvString)("COGNITO_CLIENT_SECRET", "");
        if (!clientId || !clientSecret) {
            commons_logs_1.log.debug({ fn: "OAuthClientTokenBuilder#constructor" }, "No value for clientId or clientSecret. Values will be read from SSM on first token request");
        }
        const standardConfig = OAuthClientTokenBuilder.getInternalOAuthClientConfig(directCallerServiceName, deploymentEnv);
        // TODO: For clientIdParameterName, clientSecretParameterName in a backward compatible way implement a mechanism allow these to be undefined and get them from SSM on first token request rather trying with ""
        // TODO: For accessTokenUrl simplify handling of undefined standardConfig.accessTokenUrl
        this._tokenFn = (0, ClientAuth_1.token)(AWS_REGION, config.clientIdParameterName || standardConfig.clientIdParameterName, config.clientSecretParameterName || standardConfig.clientSecretParameterName, config.accessTokenUrl || standardConfig.accessTokenUrl, clientId, clientSecret);
    }
    /**
     * Returns the token refresh function which is called by clients when get a token.
     * @returns The token refresh function.
     */
    getTokenFn() {
        return this._tokenFn;
    }
    /**
     * Get oauth client configuration for using the internalCognito service to service communication
     *
     * The directCallerServiceName is the name of the service that is instantiating clients. For example, when
     * service S1 is calling S2, the directCallerServiceName is S1.
     *
     * @param {string} directCallerServiceName - The name of the calling service that is instantiating a client
     * @param {string} env - The environment (e.g. 'prod', 'production', 'staging', 'dev') for which to retrieve the OAuth
     *   client configuration. For 'dev', the environment is overridden with 'staging' since for `dev` it is
     *   simpler to use staging for oauth in local development or dev in staging account.
     * @returns {InternalOAuthConfig} - The internal OAuth client configuration.
     *
     * @example
     * const internalOauthConfig = OAuthClientTokenBuilder.getInternalOAuthClientConfig("warehouse-events-service", getEnvString("ENV"));
     */
    static getInternalOAuthClientConfig(directCallerServiceName, env) {
        /**
         * TODO: For ssm parameter names some services are using `prod` and some services are using `production` in
         * production account. Most services are using "production". Therefore the following code is using production. We
         * need services that don't use `production` to change to use it to take advantage of this standard structure.
         */
        let convertedEnv = env;
        if (env === "prod")
            convertedEnv = "production";
        if (env === "dev")
            convertedEnv = "staging"; // dev is typically using staging
        /**
         * The same converted deployment env value is used inside the 1) Cognito domain URL and 2) ssm parameter name
         * of the client. For example the resolved values for input
         * of directCallerServiceName: "warehouse-events-service" and env: "prod" results:
         *   ssm:     "/warehouse-events-service/production/tf/COGNITO_INTERNAL_CLIENT_ID"
         *   ssm:     "/warehouse-events-service/production/tf/COGNITO_INTERNAL_CLIENT_SECRET"
         *   cognito: "https://deliverr-service-production.auth.us-east-1.amazoncognito.com/oauth2/token"
         */
        return {
            clientIdParameterName: `/${directCallerServiceName}/${convertedEnv}/tf/COGNITO_INTERNAL_CLIENT_ID`,
            clientSecretParameterName: `/${directCallerServiceName}/${convertedEnv}/tf/COGNITO_INTERNAL_CLIENT_SECRET`,
            accessTokenUrl: `https://deliverr-service-${convertedEnv}.auth.${AWS_REGION}.amazoncognito.com/oauth2/token`,
        };
    }
}
exports.OAuthClientTokenBuilder = OAuthClientTokenBuilder;
// TODO:JIRA(SMBPE-1276): Remove getInternalOAuthClientConfig once we have ensured no inflight usage
// @deprecated Use OAuthClientTokenBuilder constructor instead
exports.getInternalOAuthClientConfig = OAuthClientTokenBuilder.getInternalOAuthClientConfig;
//# sourceMappingURL=OAuthClientTokenBuilder.js.map